// src/pages/UnifiedPlayer/playerHelpers.ts
// Small, pure helper functions shared between UnifiedPlayerPage and its
// extracted subcomponents. Moved out verbatim (no logic changes) as part of
// splitting UnifiedPlayerPage.tsx into smaller, independently-verifiable
// pieces.
import { config } from '../../config';

export const getFullAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
  if (!avatarUrl) return undefined;
  if (avatarUrl.startsWith('http')) return avatarUrl;
  return `${config.backendUrl}${avatarUrl}`;
};

export const getFullMediaUrl = (mediaUrl: string | null | undefined): string | undefined => {
  if (!mediaUrl) return undefined;
  if (mediaUrl.startsWith('http')) return mediaUrl;
  return `${config.backendUrl}${mediaUrl}`;
};

export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};


/**
 * How far through a film a viewer is, as a percentage.
 *
 * <p>Pulled out of UnifiedPlayerPage so the arithmetic can be tested as arithmetic. The
 * version it replaces was:
 *
 * ```ts
 * const currentTime = Date.now();
 * completionPercentage = (currentTime / 1000) / (content.duration * 60) * 100;
 * ```
 *
 * Two errors stacked. `currentTime` was assigned `Date.now()`, shadowing the name the
 * player uses for its own position, so the numerator was Unix seconds - about 1.79
 * billion. And it multiplied the duration by 60 on the belief that the field was minutes,
 * while the same field is read as seconds elsewhere in the same file. Clamped to 100, so
 * every row that reported came out at exactly 100% complete.
 *
 * Both arguments are seconds. That is the whole point of taking them as arguments.
 */
export const completionPercent = (playedSeconds: number, totalSeconds: number): number => {
  if (!Number.isFinite(playedSeconds) || !Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return 0;
  }
  const percent = (playedSeconds / totalSeconds) * 100;
  // Clamped at both ends: a negative position is nonsense, and a browser reporting a
  // position past the end should read as finished rather than as 103%.
  return Math.min(Math.max(percent, 0), 100);
};
